import { useState, useCallback } from 'react';
import { developerApi } from '@/services/api';
import type { ApiKey, CreateApiKeyRequest, Webhook, CreateWebhookRequest } from '@/types';

interface UseDeveloperReturn {
  apiKeys: ApiKey[];
  webhooks: Webhook[];
  loading: boolean;
  error: Error | null;
  fetchApiKeys: () => Promise<void>;
  fetchWebhooks: () => Promise<void>;
  createApiKey: (data: CreateApiKeyRequest) => Promise<string | null>;
  revokeApiKey: (id: string) => Promise<boolean>;
  createWebhook: (data: CreateWebhookRequest) => Promise<Webhook | null>;
  updateWebhook: (data: { url: string; secret: string }) => Promise<boolean>;
  deleteWebhook: (id: string) => Promise<boolean>;
  testWebhook: (id: string) => Promise<boolean>;
  refetch: () => void;
}

export function useDeveloper(): UseDeveloperReturn {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchApiKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await developerApi.getApiKeys();
      if (response?.success && response.data) {
        const items = Array.isArray(response.data) ? response.data : [response.data];
        setApiKeys(items.map((k: any) => ({
          id: k.id,
          name: k.name || 'API Key',
          keyPrefix: k.prefix || '',
          keySuffix: k.suffix || '',
          permissions: typeof k.scopes === 'string' ? JSON.parse(k.scopes) : (k.scopes || []),
          rateLimit: k.rate_limit || 1000,
          lastUsedAt: k.last_used_at ? new Date(k.last_used_at).toISOString() : null,
          expiresAt: k.expires_at ? new Date(k.expires_at).toISOString() : null,
          revoked: !!k.revoked_at,
          revokedAt: k.revoked_at ? new Date(k.revoked_at).toISOString() : null,
          createdAt: new Date(k.created_at || Date.now()).toISOString(),
        })));
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch API keys'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWebhooks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await developerApi.getWebhooks();
      if (response?.success && response.data) {
        const w = response.data;
        const webhook: Webhook = {
          id: w.id || '',
          url: w.webhook_url || w.url || '',
          events: w.events || [],
          secret: w.webhook_secret || w.secret || '',
          active: true,
          lastTriggeredAt: null,
          failureCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setWebhooks([webhook]);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch webhooks'));
    } finally {
      setLoading(false);
    }
  }, []);

  const createApiKey = useCallback(async (data: CreateApiKeyRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await developerApi.createApiKey(data);
      if (response?.success && response.data) {
        const k = response.data;
        const apiKey: ApiKey = {
          id: k.id || '',
          name: data.name,
          keyPrefix: k.prefix || '',
          keySuffix: k.suffix || '',
          permissions: data.permissions,
          rateLimit: data.rateLimit || 1000,
          lastUsedAt: null,
          expiresAt: data.expiresIn ? new Date(Date.now() + data.expiresIn * 1000).toISOString() : null,
          revoked: false,
          revokedAt: null,
          createdAt: new Date().toISOString(),
        };
        setApiKeys(prev => [apiKey, ...prev]);
        return k.key || null;
      }
      throw new Error('Failed to create API key');
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to create API key'));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const revokeApiKey = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await developerApi.revokeApiKey(id);
      setApiKeys(prev => prev.map(k => k.id === id ? { ...k, revoked: true, revokedAt: new Date().toISOString() } : k));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to revoke API key'));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const createWebhook = useCallback(async () => {
    return null;
  }, []);

  const updateWebhook = useCallback(async (data: { url: string; secret: string }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await developerApi.updateWebhook(data);
      if (response?.success) {
        return true;
      }
      throw new Error('Failed to update webhook');
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update webhook'));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteWebhook = useCallback(async () => {
    return false;
  }, []);

  const testWebhook = useCallback(async () => {
    return false;
  }, []);

  const refetch = useCallback(() => {
    fetchApiKeys();
    fetchWebhooks();
  }, [fetchApiKeys, fetchWebhooks]);

  return {
    apiKeys,
    webhooks,
    loading,
    error,
    fetchApiKeys,
    fetchWebhooks,
    createApiKey,
    revokeApiKey,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    testWebhook,
    refetch,
  };
}
